/**
 * @simcheck/shared-types
 * Shared TypeScript types for SimCheck.ai platform
 */
// AT command safety policy (parser + matcher). Shared between
// server-side flow executor and any frontend that needs to pre-check
// severity before submitting a command.
export * from './atPolicy';
export * from './scriptPolicy';
export * from './timezone';
/**
 * Whether voice tests should record call audio for this org. Default
 * (undefined) → recording on; only an explicit `false` opts the tenant out.
 * Centralised so the voice executors and any future surface agree on the rule.
 */
export function isVoiceRecordingEnabledForOrg(org) {
    return org?.settings?.features?.voiceCallRecording !== false;
}
/**
 * Resolve the org-level lpac kill-switch with the canonical semantics:
 * undefined (not set) → enabled; explicit `false` → disabled; anything else
 * → enabled. Centralised so the FlowExecutor + the provisioning service
 * agree on the rule.
 */
export function isLpacEnabledForOrg(org) {
    return org?.settings?.features?.esimLpacProvisioning !== false;
}
/**
 * Whether an org is a QA-automation tenant. Mirrors `isLpacEnabledForOrg`
 * inversion: default (undefined) → NOT a QA tenant; only explicit `true`
 * unlocks the QA admin page + cross-tier primitive visibility +
 * FlowValidationService primitive-reference carve-out.
 *
 * Single source of truth for all three carve-outs (read / validate / execute)
 * — see plans/qa-automation-tenant-and-admin-page.md §2.
 */
export function isQaAutomationTenant(org) {
    return org?.settings?.features?.qaAutomation === true;
}
/** The canonical id of the default shared group — what the legacy flat inventory + the
 * first 27 promoted SIMs belong to. The id is an internal key and still spells the
 * supplier's name; renaming it would have to migrate every profile AND every org's
 * `settings.features.sharedEsimGroups` grant, so it stays. */
export const DEFAULT_SHARED_ESIM_GROUP_ID = 'monogoto';
/** The label tenants actually read for that pool. The supplier's name is ours to know, not
 * theirs to see, so the pool was renamed in the data — and every default must agree with
 * that, or a SIM promoted without an explicit name relabels the pool back. */
export const DEFAULT_SHARED_ESIM_GROUP_NAME = 'SimCheck Global';
/**
 * Whether an org has access to **any** shared eSIM group (top-level picker gate).
 * Explicit `sharedEsimGroups` (non-empty) wins; else the legacy `canUseSharedEsims
 * === true` boolean grants access. Opt-in, mirrors `isQaAutomationTenant`.
 */
export function canOrgUseSharedEsims(org) {
    const f = org?.settings?.features;
    if (Array.isArray(f?.sharedEsimGroups))
        return f.sharedEsimGroups.length > 0;
    return f?.canUseSharedEsims === true;
}
/**
 * Whether an org may use a **specific** shared group (`sharedGroupId`). Explicit
 * `sharedEsimGroups` is authoritative per-group; absent → the legacy boolean
 * grants ALL groups (grandfathered). A profile with no group is treated as the
 * default (Monogoto) group. Single source of truth for the allocator byId gate,
 * the TLAM gate, the pool resolver, and the picker.
 */
export function canUseSharedGroup(org, groupId) {
    const gid = groupId || DEFAULT_SHARED_ESIM_GROUP_ID;
    const f = org?.settings?.features;
    if (Array.isArray(f?.sharedEsimGroups))
        return f.sharedEsimGroups.includes(gid);
    return f?.canUseSharedEsims === true;
}
/**
 * Whether an eSIM profile is a super-admin shared-inventory profile. Centralised
 * so the allocator, provisioning, listing, and delete-guard all read the same
 * rule.
 */
export function isSharedProfile(profile) {
    return profile?.shared === true;
}
/**
 * Reserved eSIM selection sentinel for the **shared-inventory pool**. The eSIM
 * pickers render a single "SimCheck Shared eSIM" entry (value
 * `shared:${SHARED_ESIM_POOL_ID}`) instead of listing every shared profile;
 * the backend (`QueueService.resolveSharedPoolProfile`, called from
 * `executeFlow` + TLAM) swaps this sentinel for a concrete `shared_<iccid>`
 * profile whose holder modem is at the selected location (online, free
 * preferred). Keeps the picker at one item no matter how large the shared
 * inventory grows.
 */
export const SHARED_ESIM_POOL_ID = '__simcheck_shared_pool__';
/**
 * Build the pool sentinel for a specific shared group, e.g.
 * `__simcheck_shared_pool__:annatel`. A bare `SHARED_ESIM_POOL_ID` (no group)
 * means "any granted shared group" (back-compat). The pickers render one entry
 * per granted group using these.
 */
export function sharedPoolSentinel(groupId) {
    return groupId ? `${SHARED_ESIM_POOL_ID}:${groupId}` : SHARED_ESIM_POOL_ID;
}
/**
 * Parse a picker value / eSim id: is it the shared pool, and for which group?
 * `{ isPool:true, groupId:'annatel' }` for `__simcheck_shared_pool__:annatel`;
 * `{ isPool:true }` for the bare sentinel; `{ isPool:false }` otherwise.
 */
export function parseSharedPoolSentinel(v) {
    if (!v)
        return { isPool: false };
    if (v === SHARED_ESIM_POOL_ID)
        return { isPool: true };
    if (v.startsWith(SHARED_ESIM_POOL_ID + ':'))
        return { isPool: true, groupId: v.slice(SHARED_ESIM_POOL_ID.length + 1) };
    return { isPool: false };
}
function mfaToMillisLoose(v) {
    if (v == null)
        return null;
    if (typeof v === 'number')
        return v;
    const anyV = v;
    if (typeof anyV.toMillis === 'function')
        return anyV.toMillis();
    if (v instanceof Date)
        return v.getTime();
    if (typeof anyV.seconds === 'number')
        return anyV.seconds * 1000;
    if (typeof v === 'string') {
        const t = Date.parse(v);
        return Number.isNaN(t) ? null : t;
    }
    return null;
}
/**
 * Resolve an org's 2FA policy with canonical defaults + grace math. Single
 * source of truth shared by the client gate and the backend enforcement
 * middleware so they always agree. Mirrors `isQaAutomationTenant`'s "read the
 * flag once, in one place" pattern.
 */
export function getOrgMfaPolicy(org) {
    const p = org?.settings?.security?.mfa;
    const mode = p?.mode === 'required' ? 'required' : p?.mode === 'allowed' ? 'allowed' : 'off';
    const graceDays = typeof p?.graceDays === 'number' && p.graceDays >= 0 ? p.graceDays : 7;
    const requiredSinceMs = mfaToMillisLoose(p?.requiredSince);
    // Fail CLOSED: `required` with a missing anchor enforces immediately
    // (graceEndsAtMs = 0, already past) rather than skipping enforcement.
    const graceEndsAtMs = mode === 'required'
        ? (requiredSinceMs != null ? requiredSinceMs + graceDays * 24 * 3600 * 1000 : 0)
        : null;
    return { mode, required: mode === 'required', graceDays, requiredSinceMs, graceEndsAtMs };
}
// ============================================================================
// Token Billing + Plan Catalog (plans/token-billing-and-plan-catalog.md)
// ============================================================================
/** 1 token = up to 5 minutes of modem time. */
export const TOKEN_INTERVAL_MS = 5 * 60 * 1000;
/** States in which a managed modem can be handed to a test. */
export const CONNECTIVITY_ALLOCATABLE_STATES = new Set([
    'attached', 'hunt_attached', 'hunt_terrestrial',
]);
export const DEFAULT_CONNECTIVITY_PROBE_TARGETS = [
    { type: 'ping', target: '1.1.1.1' },
    { type: 'ping', target: '8.8.8.8' },
    { type: 'dns', target: 'www.google.com' },
    { type: 'https', target: 'https://www.gstatic.com/generate_204' },
];
/**
 * Decide whether a modem has a usable SIM physically present, from its
 * heartbeat-reported `simInfo`. A modem with no SIM can't run any test that
 * needs the network, so the allocator and the submit-time satisfiability check
 * use this to exclude SIM-less modems.
 *
 * The edge derives `simInfo.status` from `AT+CPIN?` at interrogation: a present
 * card reports `READY` (or a locked state like `SIM PIN` / `SIM PUK`), while a
 * SIM-less modem fails the read and leaves `status: 'unknown'` with an empty
 * `iccid`. A non-empty `iccid` is the strongest signal (it requires a card to
 * read). We FAIL OPEN on genuinely-missing data (`simInfo` absent, or an
 * unrecognised non-empty status) so older gateways and transient read blips
 * never block a working modem — only an explicit no-SIM signal excludes one.
 *
 * `opts.registeredOperator` is a robustness escape hatch: the edge only reads
 * `sim_status` once at interrogation and reuses the cached value on every
 * heartbeat, so a probe-time CPIN blip can leave a perfectly-working modem
 * stuck at `status:'unknown'`. A modem that is registered to a network operator
 * is demonstrably running a SIM regardless of that stale field — pass the
 * live operator and we treat it as present.
 */
export function isSimPresent(simInfo, opts) {
    // Registered to a real network ⇒ a working SIM, whatever the cached CPIN says.
    const op = (opts?.registeredOperator || '').trim();
    if (op && op.toUpperCase() !== 'N/A')
        return true;
    // No SIM data at all (older gateway / legacy path) — can't conclude absence.
    if (!simInfo)
        return true;
    const status = (simInfo.status || '').trim().toUpperCase();
    const iccid = (simInfo.iccid || '').trim();
    // A readable ICCID means a card is physically present.
    if (iccid)
        return true;
    // CPIN states that imply a card is inserted (even if PIN/PUK-locked).
    if (status.includes('READY') ||
        status.includes('PIN') ||
        status.includes('PUK') ||
        status.startsWith('PH')) {
        return true;
    }
    // Explicit absence, or the edge's "couldn't read CPIN" default of 'unknown'
    // (with no ICCID) — treat as no SIM.
    if (!status ||
        status === 'UNKNOWN' ||
        status.includes('NOT INSERTED') ||
        status.includes('NOT_INSERTED') ||
        status.includes('ABSENT') ||
        status.includes('NO SIM')) {
        return false;
    }
    // Any other non-empty status we don't recognise — fail open.
    return true;
}
/**
 * Canonical resolver — used by FlowExecutor at lock-acquire, by the admin UI
 * "effective method" badge, and by audit log payloads.
 *
 * Precedence (highest → lowest):
 *   1. `device.skipEsimProvisioning === true` → `'skip'` (hard override)
 *   2. `modem.eSimProvisioningMethod` if set
 *   3. `org.settings.defaults.eSimProvisioningMethod` if set
 *   4. fallback → `'manual'`
 */
export function resolveProvisioningMethod(input) {
    if (input.device?.skipEsimProvisioning === true) {
        return { method: 'skip', source: 'device_override' };
    }
    if (input.modem?.eSimProvisioningMethod) {
        return { method: input.modem.eSimProvisioningMethod, source: 'modem' };
    }
    const orgDefault = input.org?.settings?.defaults?.eSimProvisioningMethod;
    if (orgDefault) {
        return { method: orgDefault, source: 'org_default' };
    }
    return { method: 'manual', source: 'fallback' };
}
/**
 * Pure check: is the profile re-installable if evicted? Reuses existing
 * `downloadLimit` / `maxDownloads` / `downloadCount` fields on the profile.
 * When false, the system auto-pins the matching `installed_profiles` doc to
 * prevent destructive eviction; admin must explicitly unpin to allow loss.
 */
export function isProfileReinstallable(profile) {
    if (profile.downloadLimit === 'unlimited')
        return true;
    if (profile.downloadLimit === 'single_use')
        return (profile.downloadCount || 0) < 1;
    if (profile.downloadLimit === 'limited')
        return (profile.downloadCount || 0) < (profile.maxDownloads ?? 0);
    return false;
}
/**
 * Eviction-candidate predicate. A profile is auto-pinned (uneligible) iff it
 * cannot be reinstalled. Explicit-pinned profiles are also uneligible. The
 * currently-enabled profile is never evictable.
 */
export function isEvictable(args) {
    if (args.installed.state === 'enabled')
        return { evictable: false, reason: 'enabled' };
    if (args.installed.pinned === true)
        return { evictable: false, reason: 'pinned' };
    if (!args.sourceProfile) {
        // We can't prove the activation code is exhausted without the source
        // profile. Conservative: treat as unknown / not evictable so we never
        // destroy something we can't replace.
        return { evictable: false, reason: 'unknown_source' };
    }
    if (!isProfileReinstallable(args.sourceProfile))
        return { evictable: false, reason: 'auto_pinned' };
    return { evictable: true };
}
// Helper to convert legacy to dynamic format
export function convertLegacyCapabilities(legacy) {
    return {
        'supports5G': { type: 'boolean', value: legacy.supports5G },
        'supports4G': { type: 'boolean', value: legacy.supports4G },
        'supports3G': { type: 'boolean', value: legacy.supports3G },
        'supports2G': { type: 'boolean', value: legacy.supports2G },
        'supportsVoLTE': { type: 'boolean', value: legacy.supportsVoLTE },
        'supportseSIM': { type: 'boolean', value: legacy.supportseSIM },
        'maxDownloadSpeed': { type: 'number', value: legacy.maxDownloadSpeed, unit: 'Mbps' },
        'maxUploadSpeed': { type: 'number', value: legacy.maxUploadSpeed, unit: 'Mbps' }
    };
}
// Re-export capability definitions and helpers
export * from './capabilityDefinitions';
export * from './capabilityHelpers';
// Explicit exports for better ESM compatibility
export { STANDARD_CAPABILITIES, getCapabilityDefinition, getCapabilitiesByCategory } from './capabilityDefinitions';
// API Key types for external API access
export * from './apiKey';
// Webhook types for event notifications
export * from './webhook';
// Report types for analytics and reporting
export * from './slo';
export * from './reports';
export * from './reportBuilder';
// Reports redesign — new standalone modules.
// Order matters: types referenced by other modules come first.
export * from './ssi';
export * from './executionMetrics';
export * from './plmnTable';
export * from './mccMncOperators';
export * from './artifacts';
export * from './atErrorCodes';
export * from './clusterRun';
export * from './locationGroup';
export * from './reportShare';
export * from './executionDoc';
export * from './CatalogPrimitive';
export * from './internalExecutions';
// ============================================================================
// Error Types
// ============================================================================
export class SimCheckError extends Error {
    constructor(message, code, statusCode = 500, details) {
        super(message);
        this.code = code;
        this.statusCode = statusCode;
        this.details = details;
        this.name = 'SimCheckError';
    }
}
export class ValidationError extends SimCheckError {
    constructor(message, details) {
        super(message, 'VALIDATION_ERROR', 400, details);
        this.name = 'ValidationError';
    }
}
export class AuthenticationError extends SimCheckError {
    constructor(message, details) {
        super(message, 'AUTHENTICATION_ERROR', 401, details);
        this.name = 'AuthenticationError';
    }
}
export class AuthorizationError extends SimCheckError {
    constructor(message, details) {
        super(message, 'AUTHORIZATION_ERROR', 403, details);
        this.name = 'AuthorizationError';
    }
}
export class ResourceNotFoundError extends SimCheckError {
    constructor(resource, id) {
        super(`${resource} not found: ${id}`, 'RESOURCE_NOT_FOUND', 404);
        this.name = 'ResourceNotFoundError';
    }
}
export class ResourceAllocationError extends SimCheckError {
    constructor(message, details) {
        super(message, 'RESOURCE_ALLOCATION_ERROR', 503, details);
        this.name = 'ResourceAllocationError';
    }
}
export class ExecutionError extends SimCheckError {
    constructor(message, nodeId, details) {
        super(message, 'EXECUTION_ERROR', 500, { nodeId, ...details });
        this.name = 'ExecutionError';
    }
}
/**
 * Thrown by `ESimProvisioningService.ensureProfileReady`, `LpacAtProvider`,
 * and the SGP.32 path. Caught by `FlowExecutor`'s outer try and stamped onto
 * the execution doc as `failureCategory: 'esim_provisioning'` plus the
 * matching reason code from `EsimProvisioningErrorReason`.
 */
export class EsimProvisioningError extends SimCheckError {
    constructor(reason, message, details) {
        super(message ?? `eSIM provisioning failed: ${reason}`, `ESIM_${reason.toUpperCase()}`, 500, { reason, ...details });
        this.name = 'EsimProvisioningError';
    }
    get reason() {
        return this.details.reason;
    }
}
export const FORCED_RAT_VALUES = [
    'auto', 'nr5g', 'nr5g_nsa', 'lte', 'lte_m', 'nb_iot', 'wcdma', 'gsm',
];
export const FORCED_RAT_TO_CAPABILITY = {
    nr5g: 'supports5G',
    nr5g_nsa: 'supports5G',
    lte: 'supports4G',
    lte_m: 'supportsLTEM',
    nb_iot: 'supportsNBIoT',
    wcdma: 'supports3G',
    gsm: 'supports2G',
};
export const FORCED_RAT_LABELS = {
    auto: 'Automatic (default)',
    nr5g: '5G SA (Standalone)',
    nr5g_nsa: '5G NSA (Non-Standalone)',
    lte: '4G LTE',
    lte_m: 'LTE-M (Cat M1)',
    nb_iot: 'NB-IoT (Cat NB2)',
    wcdma: '3G (WCDMA)',
    gsm: '2G (GSM/EDGE)',
};
/**
 * Friendly labels for the modem-model capability flags used by the allocator
 * (e.g. flow's `allocationCapabilities` field). Used in user-facing error
 * messages so tenants don't see `supportsLTEM` jargon.
 */
export const CAPABILITY_LABELS = {
    supports5G: '5G',
    supports4G: '4G LTE',
    supports3G: '3G',
    supports2G: '2G',
    supportsLTEM: 'LTE-M',
    supportsNBIoT: 'NB-IoT',
    supportsVoLTE: 'VoLTE',
};
export function isForcedRat(value) {
    return typeof value === 'string' && FORCED_RAT_VALUES.includes(value);
}
export function executionContextFromConfig(config) {
    if (!config)
        return undefined;
    const src = config;
    const nested = src.configuration || {};
    const ctx = {};
    const esim = src.esim || nested.esim;
    if (esim?.mode === 'single' && esim.profileId)
        ctx.eSimProfileId = esim.profileId;
    if (esim?.mode === 'batch' && esim.batch?.groupId)
        ctx.eSimGroupId = esim.batch.groupId;
    if (esim?.allowRelocation === true)
        ctx.allowEsimRelocation = true;
    const forcedRat = src.forcedRat ?? nested.forcedRat;
    if (forcedRat && forcedRat !== 'auto')
        ctx.forcedRat = forcedRat;
    const mccmnc = src.forcedNetworkMccMnc ?? nested.forcedNetworkMccMnc;
    if (typeof mccmnc === 'string' && mccmnc.trim())
        ctx.forcedNetworkMccMnc = mccmnc.trim();
    const locReq = (src.locationRequirements || nested.locationRequirements || [])[0];
    if (locReq?.deviceId)
        ctx.forcedDeviceId = String(locReq.deviceId);
    if (locReq?.modemImei)
        ctx.requiredImei = String(locReq.modemImei);
    return Object.keys(ctx).length > 0 ? ctx : undefined;
}
export * from './esimLock';
